/*
 * Complete your look in the cart: which partners to offer and in what order
 * (docs/CART_AND_LOOKS_SPEC.md, Part B). Pure functions over plain data, so they are tested in
 * Node (test/look-rank.test.js); assets/cart-looks.js reads the data off the rendered tiles.
 *
 * A candidate is one partner product, offered by one or more cart pieces (anchors). It is offered
 * in a colour the anchors allow: an anchor's own colour first, else a colour paired with it, else
 * not at all. It is left out when it is already in the cart, or when it sells the shopper's size
 * for its role but has that size sold out. The rest go:
 *   fills the missing part of the outfit (a top with no bottom wants a bottom, and the reverse)
 *   → offered by more cart pieces → crosses the free-shipping remainder → bought together most
 *   → best seller → new → own colour before a paired one → Search & Discovery's order.
 */
(function (root) {
  const LIMIT = 8;
  const norm = (value) => `${value ?? ""}`.trim().toLowerCase();

  function wantedRoles(anchors) {
    const roles = new Set(anchors.map((anchor) => anchor.role));
    const wanted = new Set();
    if (roles.has("top") && !roles.has("bottom")) wanted.add("bottom");
    if (roles.has("bottom") && !roles.has("top")) wanted.add("top");
    return wanted;
  }

  // The shopper's sizes for a role: the cart pieces in that role, else every cart piece, else the
  // size remembered from the product page. Several sizes are all kept.
  function shopperSizes(role, anchors, remembered) {
    const same = anchors.filter((anchor) => anchor.role === role && anchor.size).map((anchor) => norm(anchor.size));
    if (same.length) return same;
    const any = anchors.filter((anchor) => anchor.size).map((anchor) => norm(anchor.size));
    if (any.length) return any;
    return remembered ? [norm(remembered)] : [];
  }

  // Sold out in the shopper's sizes: it sells at least one of them and none of those is in stock.
  // A piece in one size, or in other labels, is never ruled out by size.
  function soldOutInSize(tile, sizes) {
    const wanted = (Array.isArray(sizes) ? sizes : [sizes]).filter(Boolean);
    if (!wanted.length) return false;
    const matching = tile.sizes.filter((entry) => wanted.includes(norm(entry.value)));
    return matching.length > 0 && !matching.some((entry) => entry.available);
  }

  // The tile to offer for a partner, and its colour pass (1 own colour, 2 paired), or null.
  function chooseTile(tiles, anchorColours, pairs) {
    const colours = anchorColours.filter(Boolean);
    if (!colours.length) return { tile: tiles[0], pass: 1 };
    const own = tiles.find((tile) => !tile.colour || colours.includes(tile.colour));
    if (own) return { tile: own, pass: 1 };
    const paired = tiles.find((tile) => colours.some((colour) => pairs.includes(`|${colour}~${tile.colour}|`)));
    return paired ? { tile: paired, pass: 2 } : null;
  }

  /*
   * lists: [{ anchor, tiles: [{ productId, position, role, best, new, bought, price, colour, sizes }] }]
   * cart:  { productIds: Set of strings, anchors, remembered, remainder (cents or null), pairs }
   * Returns up to eight { tile, ... } in order.
   */
  function rank(lists, cart) {
    const wanted = wantedRoles(cart.anchors);
    const byProduct = new Map();
    for (const { anchor, tiles } of lists) {
      for (const tile of tiles) {
        if (cart.productIds.has(`${tile.productId}`)) continue;
        if (!byProduct.has(tile.productId)) byProduct.set(tile.productId, { tiles: [], anchors: new Map() });
        const entry = byProduct.get(tile.productId);
        if (!entry.tiles.some((t) => t.colour === tile.colour)) entry.tiles.push(tile);
        const previous = entry.anchors.get(anchor.product_id);
        entry.anchors.set(anchor.product_id, {
          anchor,
          bought: Math.max(previous?.bought || 0, Number(tile.bought) || 0),
          position: Math.min(previous?.position ?? Infinity, Number(tile.position) || 0),
        });
      }
    }

    const offers = [];
    for (const [productId, entry] of byProduct) {
      const anchors = [...entry.anchors.values()];
      const chosen = chooseTile(entry.tiles, anchors.map(({ anchor }) => anchor.colour), cart.pairs || "");
      if (!chosen) continue;
      const { tile, pass } = chosen;
      if (soldOutInSize(tile, shopperSizes(tile.role, cart.anchors, cart.remembered))) continue;
      offers.push({
        productId,
        tile,
        pass,
        fills: wanted.has(tile.role) ? 1 : 0,
        pieces: anchors.length,
        crosses: cart.remainder > 0 && Number(tile.price) >= cart.remainder ? 1 : 0,
        bought: anchors.reduce((sum, a) => sum + a.bought, 0),
        best: tile.best ? 1 : 0,
        fresh: tile.new ? 1 : 0,
        position: Math.min(...anchors.map((a) => a.position)),
      });
    }

    offers.sort(
      (a, b) =>
        b.fills - a.fills ||
        b.pieces - a.pieces ||
        b.crosses - a.crosses ||
        b.bought - a.bought ||
        b.best - a.best ||
        b.fresh - a.fresh ||
        a.pass - b.pass ||
        a.position - b.position,
    );
    return offers.slice(0, LIMIT);
  }

  const api = { rank, wantedRoles, shopperSizes, soldOutInSize, chooseTile, LIMIT };
  if (typeof module === "object" && module.exports) module.exports = api;
  else root.UMILookRank = api;
})(typeof globalThis !== "undefined" ? globalThis : this);
